import {Box, Typography} from '@mui/material';

import {ReactComponent as ChevronIcon} from '../../../../assets/images/chevron-base-icon.svg';
import {ReactComponent as StartEndIcon} from '../../../../assets/images/go-first-last-base-icon.svg';

import ArrIconButton from '../../../common/arr-icon-button/ArrIconButton';
import './DocPagination.scss';
import ArrTextField from '../../../common/arr-text-field/ArrTextField';
import {FC, memo, useCallback, useEffect, useState} from 'react';
import {t} from 'i18next';
import ArrModal from '../../../common/arr-modal/ArrModal';
import {isUndefined} from 'lodash';

type DocPaginationProps = {
  dataTestId?: string;
  totalCount?: number;
  defaultSelectedNumber?: number;
  entityText?: string;
  className?: string;
  onSelectionChange: (selectedNumber: number) => void;
  codingFormUpdated?: boolean;
};

const DocPagination: FC<DocPaginationProps> = ({
  dataTestId = '',
  totalCount = 0,
  defaultSelectedNumber = 0,
  entityText = '',
  className = '',
  onSelectionChange,
  codingFormUpdated = false,
}) => {
  const [selectedNumber, setSelectedNumber] = useState(0);
  const [inputValue, setInputValue] = useState('');
  const [docIdForNavigation, setDocIdForNavigation] = useState<number>();

  const navigateDoc = useCallback(
    (docId: number) => {
      if (codingFormUpdated) {
        setDocIdForNavigation(docId);
      } else {
        onSelectionChange(docId);
        setSelectedNumber(docId);
        setInputValue(`${docId}`);
      }
    },
    [onSelectionChange, codingFormUpdated]
  );

  const onStartClickHandler = useCallback(() => {
    navigateDoc(1);
  }, [navigateDoc]);

  const onPreviousClickHandler = useCallback(() => {
    navigateDoc(selectedNumber - 1);
  }, [selectedNumber, navigateDoc]);

  const onNextClickHandler = useCallback(() => {
    navigateDoc(selectedNumber + 1);
  }, [selectedNumber, navigateDoc]);

  const onEndClickHandler = useCallback(() => {
    navigateDoc(totalCount);
  }, [totalCount, navigateDoc]);

  const validateInput = useCallback(() => {
    const inputNumber = Number(inputValue);
    if (
      typeof inputNumber === 'number' &&
      !Number.isNaN(inputNumber) &&
      inputNumber != 0 &&
      inputNumber <= totalCount
    ) {
      navigateDoc(inputNumber);
    }
  }, [inputValue, navigateDoc]);

  useEffect(() => {
    setSelectedNumber(defaultSelectedNumber);
    setInputValue(`${defaultSelectedNumber}`);
  }, [defaultSelectedNumber]);
  return (
    <Box className={`doc-pagination ${className}`} data-testid={dataTestId}>
      <ArrModal
        showModal={!isUndefined(docIdForNavigation)}
        title={t('docView.headers.codingForm.loseChanges')}
        showClose
        onClose={() => {
          setDocIdForNavigation(undefined);
          setInputValue(`${defaultSelectedNumber}`);
        }}
        dataTestId="doc-pagination-modal"
        buttons={[
          {
            id: '1',
            label: t('docView.headers.codingForm.loseChangesCancel'),
            onClick: () => {
              setDocIdForNavigation(undefined);
              setInputValue(`${defaultSelectedNumber}`);
            },
            variant: 'secondary',
            dataTestId: 'dpm-cancel-btn',
          },
          {
            id: '2',
            label: t('docView.headers.codingForm.loseChangesOK'),

            onClick: () => {
              if (!isUndefined(docIdForNavigation)) {
                onSelectionChange(docIdForNavigation);
                setSelectedNumber(docIdForNavigation);
                setInputValue(`${docIdForNavigation}`);
              }
              setDocIdForNavigation(undefined);
            },
            variant: 'primary',
            dataTestId: 'dpm-ok-btn',
          },
        ]}
        description={t('docView.headers.codingForm.loseChangesOnDocSwitch')}
      ></ArrModal>
      <Box className="nav-button-container">
        <ArrIconButton
          data-testid="doc-pagination-start-button"
          className="start-button"
          size="small"
          icon={<StartEndIcon className="start-icon" />}
          ariaLabel={t('docView.docPagination.start')}
          onClick={onStartClickHandler}
          disabled={selectedNumber <= 1}
          shape={selectedNumber <= 1 ? 'none' : 'default'}
          testId="doc-pagination-start-icon-button"
        />
        <ArrIconButton
          data-testid="doc-pagination-previous-button"
          className="previous-button"
          size="small"
          icon={<ChevronIcon className="previous-icon" />}
          ariaLabel={t('docView.docPagination.previous')}
          onClick={onPreviousClickHandler}
          disabled={selectedNumber <= 1}
          shape={selectedNumber <= 1 ? 'none' : 'default'}
          testId="doc-pagination-previous-icon-button"
        />
      </Box>
      <ArrTextField
        dataTestId="doc-pagination-input-field"
        className="input-field"
        size="small"
        sizeVariant="small"
        type="text"
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            (e.target as HTMLInputElement).blur();
            validateInput();
          }
        }}
      />
      <Typography
        data-testid="doc-pagination-info"
        className="pagination-info typo-text body4_size"
      >{`of ${totalCount} ${entityText}`}</Typography>
      <Box className="nav-button-container">
        <ArrIconButton
          data-testid="doc-pagination-next-button"
          className="next-button"
          size="small"
          icon={<ChevronIcon className="next-icon" />}
          ariaLabel={t('docView.docPagination.next')}
          onClick={onNextClickHandler}
          disabled={selectedNumber >= totalCount}
          shape={selectedNumber >= totalCount ? 'none' : 'default'}
          testId="doc-pagination-next-icon-button"
        />
        <ArrIconButton
          data-testid="doc-pagination-end-button"
          className="end-button"
          size="small"
          icon={<StartEndIcon className="end-icon" />}
          ariaLabel={t('docView.docPagination.end')}
          onClick={onEndClickHandler}
          disabled={selectedNumber >= totalCount}
          shape={selectedNumber >= totalCount ? 'none' : 'default'}
          testId="doc-pagination-end-icon-button"
        />
      </Box>
    </Box>
  );
};

export default memo(DocPagination);
